import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import QuickActionPanel from '../../components/ui/QuickActionPanel';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import ProgramCard from './components/ProgramCard';
import WorkoutSession from './components/WorkoutSession';
import ProgressTracker from './components/ProgressTracker';
import AIRecommendations from './components/AIRecommendations';

const WorkoutPrograms = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('programs');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const workoutPrograms = [
  {
    id: 'prog-1',
    name: 'Strength Builder Pro',
    description: 'Comprehensive 12-week program designed to build maximum strength across all major muscle groups with progressive overload principles',
    image: "https://images.unsplash.com/photo-1674748596342-8fd299450a71",
    imageAlt: 'Muscular athlete performing barbell deadlift with perfect form in professional gym setting with weight plates',
    difficulty: 'Advanced',
    duration: '12 weeks',
    frequency: '4x/week',
    equipment: 'Full Gym',
    goal: 'Strength',
    progress: 45,
    isActive: true,
    isRecommended: false,
    category: 'strength',
    exercises: [
    {
      id: 'ex-1',
      name: 'Barbell Bench Press',
      description: 'Compound chest exercise targeting pectorals, anterior deltoids, and triceps',
      sets: 4,
      reps: '8-10',
      restTime: 90,
      equipment: 'Barbell',
      videoThumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_131be3d82-1767181066266.png",
      videoThumbnailAlt: 'Athletic man performing barbell bench press on flat bench with spotter assistance in gym'
    },
    {
      id: 'ex-2',
      name: 'Barbell Back Squat',
      description: 'Fundamental lower body compound movement for quadriceps, glutes, and hamstrings development',
      sets: 4,
      reps: '6-8',
      restTime: 120,
      equipment: 'Barbell',
      videoThumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_167d6c012-1769188932396.png",
      videoThumbnailAlt: 'Fitness athlete performing deep barbell back squat with proper form in squat rack'
    }]

  },
  {
    id: 'prog-2',
    name: 'HIIT Fat Burner',
    description: 'High-intensity interval training program combining cardio and resistance exercises for maximum calorie burn and metabolic boost',
    image: "https://images.unsplash.com/photo-1635863782730-4393246eee1e",
    imageAlt: 'Woman in athletic wear performing high-intensity interval training with battle ropes in fitness studio',
    difficulty: 'Intermediate',
    duration: '8 weeks',
    frequency: '5x/week',
    equipment: 'Minimal',
    goal: 'Fat Loss',
    progress: 62,
    isActive: false,
    isRecommended: true,
    category: 'cardio',
    exercises: []
  },
  {
    id: 'prog-3',
    name: 'Muscle Hypertrophy',
    description: 'Bodybuilding-focused program emphasizing muscle growth through volume training and time under tension',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17fa40df1-1767473706277.png",
    imageAlt: 'Muscular bodybuilder performing dumbbell shoulder press with intense focus in professional gym',
    difficulty: 'Advanced',
    duration: '16 weeks',
    frequency: '5x/week',
    equipment: 'Full Gym',
    goal: 'Muscle Gain',
    progress: 28,
    isActive: false,
    isRecommended: false,
    category: 'strength',
    exercises: []
  },
  {
    id: 'prog-4',
    name: 'Beginner Foundation',
    description: 'Perfect introduction to resistance training with focus on proper form, movement patterns, and building exercise habit',
    image: "https://images.unsplash.com/photo-1717500250423-098ada6fb7c0",
    imageAlt: 'Beginner performing bodyweight exercises on yoga mat with trainer guidance in bright fitness studio',
    difficulty: 'Beginner',
    duration: '6 weeks',
    frequency: '3x/week',
    equipment: 'Bodyweight',
    goal: 'General Fitness',
    isActive: false,
    isRecommended: true,
    category: 'general',
    exercises: []
  },
  {
    id: 'prog-5',
    name: 'Athletic Performance',
    description: 'Sport-specific training program enhancing power, speed, agility, and explosive strength for competitive athletes',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17da7624f-1766865079556.png",
    imageAlt: 'Athletic man performing explosive box jump exercise demonstrating power and agility in training facility',
    difficulty: 'Advanced',
    duration: '10 weeks',
    frequency: '4x/week',
    equipment: 'Full Gym',
    goal: 'Performance',
    isActive: false,
    isRecommended: false,
    category: 'performance',
    exercises: []
  },
  {
    id: 'prog-6',
    name: 'Yoga & Flexibility',
    description: 'Comprehensive flexibility and mobility program combining yoga poses, dynamic stretching, and breathwork',
    image: "https://images.unsplash.com/photo-1622587133988-70349a7942c8",
    imageAlt: 'Person performing advanced yoga pose on mat in peaceful studio with natural lighting and plants',
    difficulty: 'Beginner',
    duration: '8 weeks',
    frequency: '3x/week',
    equipment: 'Yoga Mat',
    goal: 'Flexibility',
    isActive: false,
    isRecommended: false,
    category: 'flexibility',
    exercises: []
  }];


  const categoryOptions = [
  { value: 'all', label: 'All Programs' },
  { value: 'strength', label: 'Strength Training' },
  { value: 'cardio', label: 'Cardio & HIIT' },
  { value: 'flexibility', label: 'Flexibility & Mobility' },
  { value: 'performance', label: 'Athletic Performance' },
  { value: 'general', label: 'General Fitness' }];


  const tabs = [
  { id: 'programs', label: 'My Programs', icon: 'Dumbbell' },
  { id: 'ai-recommendations', label: 'AI Recommendations', icon: 'Sparkles' },
  { id: 'progress', label: 'Progress Tracker', icon: 'TrendingUp' }];


  const handleStartProgram = (program) => {
    if (program?.exercises && program?.exercises?.length > 0) {
      setActiveWorkout(program);
    } else {
      alert('This program does not have exercises configured yet.');
    }
  };

  const handleCompleteWorkout = (workoutData) => {
    console.log('Workout completed:', workoutData);
    setActiveWorkout(null);
    alert('Workout completed successfully! Great job!');
  };

  const handleQuickAction = (actionId) => {
    if (actionId === 'start-workout') {
      const activeProgram = workoutPrograms?.find((p) => p?.isActive);
      if (activeProgram) {
        handleStartProgram(activeProgram);
      }
    }
  };

  const filteredPrograms = workoutPrograms?.filter((program) => {
    const matchesCategory = selectedCategory === 'all' || program?.category === selectedCategory;
    const matchesSearch = program?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    program?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Add this block - Mock progress data for ProgressTracker component
  const progressData = {
    weeklyProgress: [
    { week: 1, workouts: 3, duration: 180 },
    { week: 2, workouts: 4, duration: 240 },
    { week: 3, workouts: 3, duration: 195 },
    { week: 4, workouts: 4, duration: 260 }],

    totalWorkouts: 14,
    totalDuration: 875,
    currentStreak: 5
  };

  if (activeWorkout) {
    return (
      <WorkoutSession
        program={activeWorkout}
        onComplete={handleCompleteWorkout}
        onClose={() => setActiveWorkout(null)} />);


  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="mb-8 md:mb-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">
                Workout Programs
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                Personalized fitness routines and progress tracking
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              iconPosition="left">

              Create Custom Program
            </Button>
          </div>

          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Icon
                name="Search"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />

              <input
                type="text"
                placeholder="Search programs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full h-12 pl-12 pr-4 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />

            </div>
            <Select
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Filter by category"
              className="w-full md:w-64" />

          </div>

          <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-2">
            {tabs?.map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                  flex items-center gap-2 px-5 py-3 rounded-lg whitespace-nowrap transition-all duration-250
                  ${activeTab === tab?.id ?
              'bg-primary text-primary-foreground shadow-sm' :
              'bg-card text-foreground hover:bg-muted'}
                `
              }>

                <Icon name={tab?.icon} size={18} />
                <span className="font-medium">{tab?.label}</span>
              </button>
            )}
          </div>
        </div>

        {activeTab === 'programs' &&
        <div>
            {filteredPrograms?.length === 0 ?
          <div className="card text-center py-12">
                <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No programs found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your search or filter criteria
                </p>
                <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}>

                  Clear Filters
                </Button>
              </div> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPrograms?.map((program) =>
            <ProgramCard
              key={program?.id}
              program={program}
              onStartProgram={handleStartProgram} />

            )}
              </div>
          }
          </div>
        }

        {activeTab === 'ai-recommendations' &&
        <AIRecommendations onSelectProgram={handleStartProgram} />
        }

        {activeTab === 'progress' &&
        <ProgressTracker progressData={progressData} />
        }
      </main>
      <QuickActionPanel userRole="member" onAction={handleQuickAction} />
    </div>);

};

export default WorkoutPrograms;